/**
 * Document Upload with Cloud Integration
 *
 * This component wraps the existing MultiFileUploader with tabs to support
 * both local file uploads and cloud storage imports.
 *
 * The Cloud tab embeds CloudFileSelectContent directly in the page,
 * eliminating the need for a modal-based workflow.
 */

import React, { useState, useRef, useCallback } from "react";
import { useDismissedBatches } from "./useDismissedBatches";
import {
  Box,
  Tab,
  Tabs,
  Button,
  Alert,
  Typography,
  Collapse,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ComputerIcon from "@mui/icons-material/Computer";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { useQueryClient } from "react-query";

import MultiFileUploader from "./DocumentUpload";
import {
  CloudFileSelectContent,
  type CloudFileSelectContentRef,
  type CloudFileSelectState,
  type CloudUploadOptions,
} from "src/views/cloudFiles/components/CloudFileSelectContent";
import {
  useStartBatchUpload,
  useActiveBatches,
  createBatchUploadRequest,
  partitionBatches,
} from "src/views/cloudFiles/services/CloudUploadServices";
import { CloudBatchStatusCard } from "./CloudBatchStatusCard";
import type {
  CloudProviderType,
  CloudFileMetadata,
} from "src/types/cloudFiles";

// =============================================================================
// Types
// =============================================================================

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

interface DocumentUploadWithCloudProps {
  titleHide?: boolean;
  datasetId?: number | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataset?: any;
  onDoneClick?: () => void;
  doneCallback?: () => void;
  fileType?: string;
  onChange?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadedFiles?: any[];
  deleteFunction?: (id: number) => void;
  softDeleteProjectFileStatus?: string;
  context?: string;
  /** Show cloud tab (default: true) */
  showCloudTab?: boolean;
  /** Initial tab (0 = local, 1 = cloud) */
  initialTab?: number;
}

// =============================================================================
// Tab Panel Component
// =============================================================================

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`upload-tabpanel-${index}`}
      aria-labelledby={`upload-tab-${index}`}
      style={{ height: value === index ? "100%" : 0 }}
      {...other}
    >
      {value === index && <Box sx={{ height: "100%" }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `upload-tab-${index}`,
    "aria-controls": `upload-tabpanel-${index}`,
  };
}

// =============================================================================
// Main Component
// =============================================================================

export const DocumentUploadWithCloud: React.FC<
  DocumentUploadWithCloudProps
> = ({
  showCloudTab = true,
  initialTab = 0,
  datasetId: propDatasetId,
  ...multiFileUploaderProps
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [cloudSelectionState, setCloudSelectionState] =
    useState<CloudFileSelectState | null>(null);
  const [cloudUploadError, setCloudUploadError] = useState<string | null>(null);
  const cloudSelectRef = useRef<CloudFileSelectContentRef>(null);
  const queryClient = useQueryClient();

  // Get dataset ID from props or route params
  const params = useParams<{ dataset_id: string }>();
  const datasetId = propDatasetId ?? params.dataset_id;
  const numericDatasetId =
    typeof datasetId === "string" ? parseInt(datasetId, 10) : datasetId;

  // Batch upload mutation
  const { mutate: startBatchUpload, isLoading: isStartingUpload } =
    useStartBatchUpload(numericDatasetId!);

  // Active + recent batches query - poll for status
  const { data: activeBatchesData, isLoading: isLoadingBatches } =
    useActiveBatches(numericDatasetId!, !!numericDatasetId);
  const { dismissedBatchIds, dismissBatch } =
    useDismissedBatches(numericDatasetId);
  const allBatches = activeBatchesData?.batches ?? [];
  const { active: activeBatches, recent: recentBatches } = partitionBatches(
    allBatches,
    dismissedBatchIds,
  );

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Track cloud selection state
  const handleCloudSelectionChange = useCallback(
    (state: CloudFileSelectState) => {
      setCloudSelectionState(state);
    },
    [],
  );

  // Handle cloud upload
  const handleCloudUpload = useCallback(
    (
      providerType: CloudProviderType,
      connectionId: number,
      files: CloudFileMetadata[],
      folders: CloudFileMetadata[],
      options: CloudUploadOptions,
    ) => {
      if (!numericDatasetId) {
        setCloudUploadError("Dataset ID is required");
        return;
      }

      setCloudUploadError(null);

      // Build the batch upload request
      const allItems = [
        ...files.map((f) => ({ path: f.path, is_directory: false })),
        ...folders.map((f) => ({ path: f.path, is_directory: true })),
      ];

      const request = createBatchUploadRequest(
        providerType,
        connectionId,
        allItems,
        options.folderOptions,
      );

      startBatchUpload(request, {
        onSuccess: () => {
          // Clear selection after successful start
          cloudSelectRef.current?.clearSelection();
          // Immediately refetch active batches so the tile appears right away
          queryClient.invalidateQueries(["cloud-batches", numericDatasetId]);
        },
        onError: (error) => {
          const errorMessage =
            error instanceof Error ? error.message : "Upload failed";
          setCloudUploadError(errorMessage);
        },
      });
    },
    [numericDatasetId, startBatchUpload, queryClient],
  );

  // Handle upload button click (uses current selection from ref)
  const handleUploadClick = useCallback(() => {
    const state = cloudSelectRef.current?.getState();
    if (!state || !state.selectedProvider || !state.selectedConnectionId)
      return;

    handleCloudUpload(
      state.selectedProvider,
      state.selectedConnectionId,
      state.selectedFiles,
      state.selectedFolders,
      { folderOptions: state.folderOptions },
    );
  }, [handleCloudUpload]);

  const canUpload =
    cloudSelectionState?.canUpload && cloudSelectionState?.canUseCloud;
  const totalSelected = cloudSelectionState?.totalSelected ?? 0;

  // Show brief loading state while checking for active batches
  if (isLoadingBatches) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 200,
          gap: 2,
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Tabs */}
      {showCloudTab && (
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="upload source tabs"
            data-testid="cloud-upload-tabs"
          >
            <Tab
              icon={<ComputerIcon />}
              iconPosition="start"
              label="Local Files"
              data-testid="cloud-upload-tab-local"
              data-attr="datasets:tab:upload-local-files"
              {...a11yProps(0)}
            />
            <Tab
              icon={<CloudUploadIcon />}
              iconPosition="start"
              label="Cloud Storage"
              data-testid="cloud-upload-tab-cloud"
              data-attr="datasets:tab:upload-cloud-storage"
              {...a11yProps(1)}
            />
          </Tabs>
        </Box>
      )}

      {/* Tab Panels */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {/* Local Files Tab */}
        <TabPanel
          value={activeTab}
          index={0}
          data-testid="cloud-upload-panel-local"
        >
          <MultiFileUploader
            {...multiFileUploaderProps}
            datasetId={datasetId}
          />
        </TabPanel>

        {/* Cloud Storage Tab */}
        {showCloudTab && (
          <TabPanel
            value={activeTab}
            index={1}
            data-testid="cloud-upload-panel-cloud"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 500,
              }}
            >
              {/* Error display */}
              {cloudUploadError && (
                <Alert
                  severity="error"
                  sx={{ m: 2 }}
                  onClose={() => setCloudUploadError(null)}
                  data-testid="cloud-upload-error"
                >
                  {cloudUploadError}
                </Alert>
              )}

              {/* Cloud file selector */}
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <CloudFileSelectContent
                  ref={cloudSelectRef}
                  datasetId={numericDatasetId}
                  onSelectionChange={handleCloudSelectionChange}
                  onUpload={handleCloudUpload}
                />
              </Box>

              {/* Upload action bar */}
              <Box
                sx={{
                  p: 2,
                  borderTop: 1,
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {totalSelected > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {totalSelected} item{totalSelected !== 1 ? "s" : ""}{" "}
                    selected
                  </Typography>
                )}
                <Button
                  variant="contained"
                  onClick={handleUploadClick}
                  disabled={!canUpload || isStartingUpload}
                  startIcon={<CloudUploadIcon />}
                  data-testid="cloud-upload-btn"
                  data-attr="datasets:btn:import-from-cloud"
                >
                  {isStartingUpload
                    ? "Starting Import..."
                    : `Import ${
                        totalSelected > 0 ? totalSelected : ""
                      } from Cloud`}
                </Button>
              </Box>

              {/* Active cloud batches - shown below form */}
              <Collapse in={activeBatches.length > 0} timeout={300}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                  data-testid="cloud-batch-list"
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Active Cloud Imports
                  </Typography>
                  {activeBatches.map((batch) => (
                    <CloudBatchStatusCard
                      key={batch.batch_id}
                      batch={batch}
                      datasetId={numericDatasetId!}
                    />
                  ))}
                </Box>
              </Collapse>

              {/* Recently completed cloud imports - persistent history */}
              <Collapse in={recentBatches.length > 0} timeout={300}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                  data-testid="cloud-recent-batch-list"
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Recent Imports
                  </Typography>
                  {recentBatches.map((batch) => (
                    <CloudBatchStatusCard
                      key={batch.batch_id}
                      batch={batch}
                      datasetId={numericDatasetId!}
                      onDismiss={dismissBatch}
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          </TabPanel>
        )}
      </Box>
    </Box>
  );
};

DocumentUploadWithCloud.propTypes = {
  titleHide: PropTypes.bool,
  datasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dataset: PropTypes.object,
  onDoneClick: PropTypes.func,
  doneCallback: PropTypes.func,
  fileType: PropTypes.string,
  onChange: PropTypes.func,
  uploadedFiles: PropTypes.arrayOf(PropTypes.object),
  deleteFunction: PropTypes.func,
  softDeleteProjectFileStatus: PropTypes.string,
  context: PropTypes.string,
  showCloudTab: PropTypes.bool,
  initialTab: PropTypes.number,
};

export default DocumentUploadWithCloud;
